import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import ClearIcon from '@mui/icons-material/Clear';
import {
  getOperatorActivity,
  updateOperatorActivity,
  getOperatorNames,
  populateOperatorActivity,
} from '../api';

interface OperatorActivity {
  id: number;
  operator_name: string;
  date: string;
  hours_on_line: number;
  hours_from_calls: number;
  hours_manual_override: number | null;
  calls_count: number;
}

const numberFormatter = new Intl.NumberFormat('ru-RU', {
  maximumFractionDigits: 2,
});

export const OperatorHoursPage: React.FC = () => {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [operatorFilter, setOperatorFilter] = useState<string>('');
  const [operatorNames, setOperatorNames] = useState<string[]>([]);
  const [activities, setActivities] = useState<OperatorActivity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState<OperatorActivity | null>(null);
  const [editHours, setEditHours] = useState<string>('');

  // Populate dialog state
  const [populateDialogOpen, setPopulateDialogOpen] = useState(false);
  const [populateFrom, setPopulateFrom] = useState('');
  const [populateTo, setPopulateTo] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [activityData, namesData] = await Promise.all([
        getOperatorActivity(operatorFilter || undefined, month),
        getOperatorNames(),
      ]);
      setActivities(activityData);
      setOperatorNames(namesData);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [month, operatorFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEditOpen = (activity: OperatorActivity) => {
    setEditingActivity(activity);
    setEditHours(activity.hours_manual_override?.toString() || activity.hours_on_line.toString());
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editingActivity) return;
    try {
      await updateOperatorActivity(editingActivity.id, {
        hours_manual_override: editHours ? Number(editHours) : null,
      });
      setEditDialogOpen(false);
      setEditingActivity(null);
      setSuccess('Часы обновлены');
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to update hours');
    }
  };

  const handleClearOverride = async (activity: OperatorActivity) => {
    if (!window.confirm('Сбросить ручную корректировку и использовать расчётные часы?')) return;
    try {
      await updateOperatorActivity(activity.id, { hours_manual_override: null });
      setSuccess('Корректировка сброшена');
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to clear override');
    }
  };

  const handlePopulate = async () => {
    if (!populateFrom || !populateTo) {
      setError('Укажите даты начала и конца периода');
      return;
    }
    setLoading(true);
    try {
      const result = await populateOperatorActivity(populateFrom, populateTo);
      setSuccess(`Обработано ${result.processedDays} дней, ${result.totalOperators} записей операторов`);
      setPopulateDialogOpen(false);
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to populate activity');
    } finally {
      setLoading(false);
    }
  };

  // Group activities by operator
  const groupedActivities: Record<string, OperatorActivity[]> = {};
  for (const act of activities) {
    if (!groupedActivities[act.operator_name]) {
      groupedActivities[act.operator_name] = [];
    }
    groupedActivities[act.operator_name].push(act);
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Часы работы операторов</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Период"
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            size="small"
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Оператор</InputLabel>
            <Select
              value={operatorFilter}
              label="Оператор"
              onChange={(e) => setOperatorFilter(e.target.value)}
            >
              <MenuItem value="">Все операторы</MenuItem>
              {operatorNames.map((name) => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => setPopulateDialogOpen(true)}
          >
            Пересчитать из звонков
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Оператор</TableCell>
                <TableCell>Дата</TableCell>
                <TableCell align="right">
                  <Tooltip title="Часы, рассчитанные из длительности звонков">
                    <span>Часы (звонки)</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Ручная корректировка часов (если задана)">
                    <span>Корректировка</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Итоговые часы для расчёта ЗП">
                    <span>Итого часов</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">Звонков</TableCell>
                <TableCell align="center">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activities.map((act) => (
                <TableRow
                  key={act.id}
                  sx={{
                    backgroundColor: act.hours_manual_override !== null ? 'action.selected' : undefined,
                  }}
                >
                  <TableCell>{act.operator_name}</TableCell>
                  <TableCell>{new Date(act.date).toLocaleDateString('ru-RU')}</TableCell>
                  <TableCell align="right">
                    {numberFormatter.format(act.hours_from_calls)}
                  </TableCell>
                  <TableCell align="right">
                    {act.hours_manual_override !== null ? (
                      <Typography component="span" color="primary">
                        {numberFormatter.format(act.hours_manual_override)}
                      </Typography>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {numberFormatter.format(act.hours_on_line)}
                  </TableCell>
                  <TableCell align="right">{act.calls_count}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleEditOpen(act)} title="Редактировать">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {act.hours_manual_override !== null && (
                      <IconButton
                        size="small"
                        onClick={() => handleClearOverride(act)}
                        title="Сбросить корректировку"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {activities.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Нет данных за выбранный период
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Редактировать часы</DialogTitle>
        <DialogContent>
          {editingActivity && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Оператор: {editingActivity.operator_name}
                <br />
                Дата: {new Date(editingActivity.date).toLocaleDateString('ru-RU')}
                <br />
                Часы из звонков: {numberFormatter.format(editingActivity.hours_from_calls)}
              </Typography>
              <TextField
                label="Часы работы"
                type="number"
                value={editHours}
                onChange={(e) => setEditHours(e.target.value)}
                fullWidth
                inputProps={{ step: 0.1 }}
                helperText="Оставьте пустым, чтобы использовать часы из звонков"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleEditSave} variant="contained">Сохранить</Button>
        </DialogActions>
      </Dialog>

      {/* Populate Dialog */}
      <Dialog open={populateDialogOpen} onClose={() => setPopulateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Пересчитать активность из звонков</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="textSecondary">
              Данные будут агрегированы из таблицы call_scoring_v2 за указанный период.
              Существующие записи будут обновлены (кроме ручных корректировок).
            </Typography>
            <TextField
              label="Дата начала"
              type="date"
              value={populateFrom}
              onChange={(e) => setPopulateFrom(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Дата окончания"
              type="date"
              value={populateTo}
              onChange={(e) => setPopulateTo(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPopulateDialogOpen(false)}>Отмена</Button>
          <Button onClick={handlePopulate} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Пересчитать'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OperatorHoursPage;
